/**
 * The PDF rule shared by both tab InputTypes.
 *
 * Neither Print template emits a tab strip, but the group hosting the selector still renders its
 * wrapper, so every tabbed section reserved an empty band (12-30px depending on width) above its
 * content. The Print templates mark their hidden input; this collapses the group around it -- and
 * the whole section when the tab host is all that section holds.
 *
 * `Pict-Input-TabGroupSelector` and `Pict-Input-TabSectionSelector` extend different pict-section-form
 * providers and share no base class, so a module is what keeps the two in step -- same reasoning as
 * _FilterToggleUI.js.
 *
 * The class name says "group" for the same reason it always did: it shipped that way, and the marker
 * is matched only by the rule below. It covers BOTH tab InputTypes.
 */

/** @type {String} */
const _PrintHostMarkerClass = 'pict-tab-group-print-host';

/**
 * `:only-child` twice -- on the columns row and on the input -- so this collapses a group ONLY when
 * that hidden input is the entire content. A manifest that puts other descriptors alongside the tab
 * selector keeps them. A renderer without `:has()` drops the rule and gets the old spacing back, so
 * this cannot regress a PDF.
 *
 * @type {String}
 */
const _PrintHostGroupCSS = `.pict-form-group:has(> .section-content > .columns:only-child > .${_PrintHostMarkerClass}:only-child) { display: none; }`;

/**
 * Some manifests host the selector in a section of its own -- ProjectTransition's `Tabs` section is
 * nothing but `UI.ActiveTab`. Collapsing the group there leaves the section's own `<h2>` behind, so
 * the PDF gets a heading ("Sections") with nothing under it, which is worse than the blank band.
 *
 * `:only-of-type` on the group is what keeps this narrow: it matches a section whose ONLY group is
 * the tab host. Measured both ways -- ProjectTransition's Tabs section collapses (48px -> 0), and
 * Sample Creation 391's Testing section matches ZERO because it has three groups.
 *
 * @type {String}
 */
const _PrintHostSectionCSS = `.pict-form-section:has(> .pict-form-group:only-of-type > .section-content > .columns:only-child > .${_PrintHostMarkerClass}:only-child) { display: none; }`;

/** @type {String} */
const _PrintHostCSS = `${_PrintHostGroupCSS} ${_PrintHostSectionCSS}`;

/** @type {String} */
const _PrintHostCSSHash = 'Headlight-Input-TabSelector-PrintHost';

/**
 * Register the fragment WITHOUT injecting it.
 *
 * CSSMap.addCSS raises the dirty flag while injectCSS lowers it whether or not a style element
 * exists yet, so injecting at construction can silently drop the fragment. Register early, inject
 * at init. Re-registering under the same hash is harmless, so both providers may call this.
 *
 * @param {Object} pPict - The Pict instance.
 *
 * @return {void}
 */
function registerPrintHostCSS(pPict)
{
	pPict.CSSMap.addCSS(_PrintHostCSSHash, _PrintHostCSS, 1002, 'Pict-Input-TabSelector');
}

/**
 * Inject whatever is registered. Call this ABOVE a provider's non-interactive early return -- the
 * PDF is the one path that needs this rule.
 *
 * @param {Object} pPict - The Pict instance.
 *
 * @return {void}
 */
function injectPrintHostCSS(pPict)
{
	pPict.CSSMap.injectCSS();
}

module.exports = {
	PrintHostMarkerClass: _PrintHostMarkerClass,
	PrintHostCSS: _PrintHostCSS,
	PrintHostGroupCSS: _PrintHostGroupCSS,
	PrintHostSectionCSS: _PrintHostSectionCSS,
	PrintHostCSSHash: _PrintHostCSSHash,
	registerPrintHostCSS,
	injectPrintHostCSS,
};
